import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  useLazyGetPropertySearchIdsQuery,
  useLazyGetPropertyByIdQuery,
} from "../Services/auto-complete.services";

// Types
export interface PropertyPreview {
  id: string;
  isLoaded: boolean;
  isLoading: boolean;
  data: any | null;
  error: any | null;
}

interface UseInfinitePropertyScrollOptions {
  filterType: string;
  filterValue: string;
  minBeds?: string;
  minBaths?: string;
  minYearBuilt?: number;
  maxYearBuilt?: number;
  sortBy?: string;
  // Amenity filters
  hasAttic?: boolean;
  hasBasement?: boolean;
  hasDeck?: boolean;
  hasBalcony?: boolean;
  hasGarage?: boolean;
  hasPatio?: boolean;
  hasPool?: boolean;
  hasAirConditioning?: boolean;
  // Pagination state from URL
  initialPage?: number;
  onPageChange?: (page: number) => void;
}

interface UseInfinitePropertyScrollReturn {
  properties: PropertyPreview[];
  isInitialLoading: boolean;
  isFilterLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  totalCount: number;
  error: any | null;
  loadMoreRef: (node: HTMLElement | null) => void;
  retry: () => void;
  shouldShowLoadMoreButton: boolean;
  loadMore: () => void;
  currentPage: number;
}

// Constants
const DISPLAY_BATCH_SIZE = 15; // Show 15 properties at a time

// Create a stable key from filter params
function createFilterKey(params: {
  filterType: string;
  filterValue: string;
  minBeds?: string;
  minBaths?: string;
  minYearBuilt?: number;
  maxYearBuilt?: number;
  sortBy?: string;
  hasAttic?: boolean;
  hasBasement?: boolean;
  hasDeck?: boolean;
  hasBalcony?: boolean;
  hasGarage?: boolean;
  hasPatio?: boolean;
  hasPool?: boolean;
  hasAirConditioning?: boolean;
}): string {
  return JSON.stringify(params);
}

export function useInfinitePropertyScroll({
  filterType,
  filterValue,
  minBeds,
  minBaths,
  minYearBuilt,
  maxYearBuilt,
  sortBy,
  hasAttic,
  hasBasement,
  hasDeck,
  hasBalcony,
  hasGarage,
  hasPatio,
  hasPool,
  hasAirConditioning,
  initialPage = 0,
  onPageChange,
}: UseInfinitePropertyScrollOptions): UseInfinitePropertyScrollReturn {
  // Create a stable filter key to detect changes
  const filterKey = useMemo(
    () =>
      createFilterKey({
        filterType,
        filterValue,
        minBeds,
        minBaths,
        minYearBuilt,
        maxYearBuilt,
        sortBy,
        hasAttic,
        hasBasement,
        hasDeck,
        hasBalcony,
        hasGarage,
        hasPatio,
        hasPool,
        hasAirConditioning,
      }),
    [filterType, filterValue, minBeds, minBaths, minYearBuilt, maxYearBuilt, sortBy, hasAttic, hasBasement, hasDeck, hasBalcony, hasGarage, hasPatio, hasPool, hasAirConditioning]
  );

  // Core state
  const [propertyIds, setPropertyIds] = useState<string[]>([]); // All IDs fetched so far
  const [propertyCache, setPropertyCache] = useState<Map<string, any>>(new Map());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [displayCount, setDisplayCount] = useState(DISPLAY_BATCH_SIZE); // How many properties to show
  const [totalCount, setTotalCount] = useState(0);
  const [nextResultIndex, setNextResultIndex] = useState(0); // For pagination: next page starts at this index
  const [apiHasMore, setApiHasMore] = useState(true); // Whether API has more pages

  // Loading states
  const [status, setStatus] = useState<'idle' | 'loading' | 'filtering' | 'ready' | 'error'>('idle');
  const [error, setError] = useState<any>(null);

  // Track which batch of 15 we're showing within current 30 IDs
  // true = showing first 15, needs to scroll for second 15
  // false = showing all 30, needs Load More button for next API call
  const [showingFirstHalf, setShowingFirstHalf] = useState(true);

  // Track current page (0-indexed, each page = 30 IDs from API)
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Refs for preventing race conditions
  const currentFilterKeyRef = useRef(filterKey);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isFetchingRef = useRef(false);
  const isFetchingDetailsRef = useRef(false);

  // Track failed property IDs to avoid infinite retries
  const failedIdsRef = useRef<Set<string>>(new Set());
  const retryCountRef = useRef<Map<string, number>>(new Map());

  // RTK Query hooks
  const [fetchPropertyIds] = useLazyGetPropertySearchIdsQuery();
  const [fetchPropertyById] = useLazyGetPropertyByIdQuery();

  // Reset everything when filters change
  useEffect(() => {
    currentFilterKeyRef.current = filterKey;

    // Reset state
    setPropertyIds([]);
    setPropertyCache(new Map());
    setLoadingIds(new Set());
    setDisplayCount(DISPLAY_BATCH_SIZE);
    setTotalCount(0);
    setNextResultIndex(0);
    setApiHasMore(true);
    setShowingFirstHalf(true);
    setCurrentPage(0);
    setError(null);
    isFetchingRef.current = false;
    isFetchingDetailsRef.current = false;

    // Clear failed IDs tracking
    failedIdsRef.current.clear();
    retryCountRef.current.clear();

    // Set loading status
    setStatus(filterType && filterValue ? 'filtering' : 'idle');
  }, [filterKey, filterType, filterValue]);

  // Fetch property IDs from API
  const fetchIds = useCallback(
    async (resultIndex: number, currentKey: string) => {
      if (!filterType || !filterValue) {
        return null;
      }

      try {
        const result = await fetchPropertyIds({
          filterType,
          filterValue,
          resultIndex,
          minBeds,
          minBaths,
          minYearBuilt,
          maxYearBuilt,
          sortBy,
          hasAttic,
          hasBasement,
          hasDeck,
          hasBalcony,
          hasGarage,
          hasPatio,
          hasPool,
          hasAirConditioning,
        }).unwrap();

        // Check if filter changed during fetch
        if (currentFilterKeyRef.current !== currentKey) {
          return null; // Discard stale results
        }

        return result;
      } catch (err) {
        if (currentFilterKeyRef.current !== currentKey) {
          return null;
        }
        throw err;
      }
    },
    [fetchPropertyIds, filterType, filterValue, minBeds, minBaths, minYearBuilt, maxYearBuilt, sortBy, hasAttic, hasBasement, hasDeck, hasBalcony, hasGarage, hasPatio, hasPool, hasAirConditioning]
  );

  // Fetch a single property with retry logic
  const fetchSingleProperty = useCallback(
    async (id: string): Promise<{ id: string; data: any | null; error: any | null; permanentlyFailed: boolean }> => {
      // Skip if already permanently failed
      if (failedIdsRef.current.has(id)) {
        return { id, data: null, error: 'Previously failed', permanentlyFailed: true };
      }

      const currentRetryCount = retryCountRef.current.get(id) || 0;

      try {
        const data = await fetchPropertyById(id).unwrap();
        // Success - clear retry count
        retryCountRef.current.delete(id);
        return { id, data, error: null, permanentlyFailed: false };
      } catch (error) {
        if (currentRetryCount < 1) {
          // First failure - retry once
          retryCountRef.current.set(id, currentRetryCount + 1);

          // Wait a bit before retry
          await new Promise(resolve => setTimeout(resolve, 500));

          try {
            const data = await fetchPropertyById(id).unwrap();
            // Retry succeeded
            retryCountRef.current.delete(id);
            return { id, data, error: null, permanentlyFailed: false };
          } catch (retryError) {
            // Retry also failed - mark as permanently failed
            failedIdsRef.current.add(id);
            retryCountRef.current.delete(id);
            return { id, data: null, error: retryError, permanentlyFailed: true };
          }
        } else {
          // Already retried - mark as permanently failed
          failedIdsRef.current.add(id);
          retryCountRef.current.delete(id);
          return { id, data: null, error, permanentlyFailed: true };
        }
      }
    },
    [fetchPropertyById]
  );

  // Fetch property details for a batch of IDs
  const fetchPropertyDetails = useCallback(
    async (ids: string[], currentKey: string) => {
      if (ids.length === 0) return;

      // Filter out already failed IDs
      const idsToFetch = ids.filter(id => !failedIdsRef.current.has(id));
      if (idsToFetch.length === 0) return;

      // Mark as loading
      setLoadingIds((prev) => {
        const next = new Set(prev);
        idsToFetch.forEach((id) => next.add(id));
        return next;
      });

      // Fetch all in parallel with retry logic
      const results = await Promise.all(
        idsToFetch.map((id) => fetchSingleProperty(id))
      );

      // Check if filter changed during fetch
      if (currentFilterKeyRef.current !== currentKey) {
        return;
      }

      // Collect permanently failed IDs
      const permanentlyFailedIds = results
        .filter(r => r.permanentlyFailed)
        .map(r => r.id);

      // Remove permanently failed IDs from propertyIds
      if (permanentlyFailedIds.length > 0) {
        setPropertyIds((prev) => prev.filter(id => !permanentlyFailedIds.includes(id)));
      }

      // Update cache with successful results
      setPropertyCache((prev) => {
        const next = new Map(prev);
        results.forEach(({ id, data }) => {
          if (data) next.set(id, data);
        });
        return next;
      });

      // Remove from loading
      setLoadingIds((prev) => {
        const next = new Set(prev);
        idsToFetch.forEach((id) => next.delete(id));
        return next;
      });
    },
    [fetchSingleProperty]
  );

  // Initial fetch when filters change (Page Init)
  useEffect(() => {
    if (!filterType || !filterValue || status !== 'filtering') return;

    const currentKey = filterKey;

    const loadInitial = async () => {
      try {
        isFetchingRef.current = true;

        let allIds: string[] = [];
        let lastResultIndex = 0;
        let lastHasMore = true;
        let lastTotalCount = 0;

        // Load pages 0 through initialPage to restore state
        const pagesToLoad = initialPage + 1;

        for (let page = 0; page < pagesToLoad && lastHasMore; page++) {
          const resultIndex = page * 30; // Each page is 30 IDs

          const result = await fetchIds(resultIndex, currentKey);

          // If filter changed during fetch, result will be null - just return
          if (!result) {
            isFetchingRef.current = false;
            return;
          }

          const { propertyIds: rawIds = [], totalCount: count = 0, resultIndex: nextIndex = 0, hasMore = false } = result;

          // Deduplicate and accumulate IDs
          const existingIds = new Set(allIds);
          const uniqueNewIds = rawIds.filter((id: string) => !existingIds.has(id));
          allIds = [...allIds, ...uniqueNewIds];
          lastResultIndex = nextIndex;
          lastHasMore = hasMore;
          lastTotalCount = count;

          // If no more data, stop loading
          if (!hasMore || rawIds.length === 0) break;
        }

        setPropertyIds(allIds);
        setTotalCount(lastTotalCount);
        setNextResultIndex(lastResultIndex);
        setApiHasMore(lastHasMore);
        setCurrentPage(initialPage);

        // Determine display state based on initialPage
        // If we loaded multiple pages, show all of them
        if (initialPage > 0) {
          // Only set showingFirstHalf to false if API actually has more data
          setShowingFirstHalf(!lastHasMore);
          setDisplayCount(allIds.length); // Show all loaded IDs
        } else {
          setShowingFirstHalf(true);
          setDisplayCount(Math.min(DISPLAY_BATCH_SIZE, allIds.length));
        }

        if (allIds.length === 0) {
          setStatus('ready');
          isFetchingRef.current = false;
          return;
        }

        // Fetch property details for visible items
        const visibleCount = initialPage > 0 ? allIds.length : Math.min(DISPLAY_BATCH_SIZE, allIds.length);
        const idsToFetch = allIds.slice(0, visibleCount);
        await fetchPropertyDetails(idsToFetch, currentKey);

        if (currentFilterKeyRef.current === currentKey) {
          setStatus('ready');
        }

        isFetchingRef.current = false;
      } catch (err) {
        if (currentFilterKeyRef.current === currentKey) {
          setError(err);
          setStatus('error');
        }
        isFetchingRef.current = false;
      }
    };

    loadInitial();
  }, [filterType, filterValue, status, filterKey, fetchIds, fetchPropertyDetails, initialPage]);

  // Load more property details when displayCount increases (for scroll loading second half)
  useEffect(() => {
    if (status !== 'ready' || propertyIds.length === 0 || isFetchingDetailsRef.current) return;

    const currentKey = filterKey;
    const idsToShow = propertyIds.slice(0, displayCount);
    const idsNotLoaded = idsToShow.filter(
      (id) => !propertyCache.has(id) && !loadingIds.has(id)
    );

    if (idsNotLoaded.length > 0) {
      isFetchingDetailsRef.current = true;
      fetchPropertyDetails(idsNotLoaded, currentKey).finally(() => {
        isFetchingDetailsRef.current = false;
      });
    }
  }, [displayCount, propertyIds, propertyCache, loadingIds, status, filterKey, fetchPropertyDetails]);

  // Intersection Observer for infinite scroll (loads second batch of 15 within current 30)
  const loadMoreRef = useCallback(
    (node: HTMLElement | null) => {
      // Disconnect previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || status !== 'ready') return;

      // Only auto-scroll if we're showing first half and there's a second half to show
      const hasSecondHalfToShow = showingFirstHalf && displayCount < propertyIds.length;
      if (!hasSecondHalfToShow) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && showingFirstHalf && !isFetchingDetailsRef.current) {
            // Load second batch of 15 (indices 15-29)
            const newDisplayCount = Math.min(displayCount + DISPLAY_BATCH_SIZE, propertyIds.length);
            setDisplayCount(newDisplayCount);

            // If we've shown all 30, mark that we need Load More button
            if (newDisplayCount >= propertyIds.length) {
              setShowingFirstHalf(false);
            }
          }
        },
        { threshold: 0.1, rootMargin: "200px" }
      );

      observerRef.current.observe(node);
    },
    [status, displayCount, propertyIds.length, showingFirstHalf]
  );

  // Manual load more function (fetches next 30 IDs from API)
  const loadMore = useCallback(async () => {
    if (isFetchingRef.current || !apiHasMore) return;

    const currentKey = currentFilterKeyRef.current;
    isFetchingRef.current = true;

    try {
      // Fetch next batch of 30 IDs
      const result = await fetchIds(nextResultIndex, currentKey);
      if (!result) {
        isFetchingRef.current = false;
        return;
      }

      const { propertyIds: newIds = [], resultIndex = nextResultIndex, hasMore = false } = result;

      if (currentFilterKeyRef.current === currentKey && newIds.length > 0) {
        // Append new IDs to existing list
        setPropertyIds((prev) => {
          const combined = [...prev, ...newIds];
          return Array.from(new Set(combined)) as string[];
        });
        setNextResultIndex(resultIndex);
        setApiHasMore(hasMore);
        setShowingFirstHalf(true); // Reset to show first 15 of new batch

        // Increment page and notify parent
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        onPageChange?.(newPage);

        // Immediately start loading first 15 of new batch
        const currentIds = propertyIds;
        const startIndex = currentIds.length;
        const firstBatchOfNewIds = newIds.slice(0, DISPLAY_BATCH_SIZE);

        // Update display count to show the first 15 of the new batch
        setDisplayCount(startIndex + DISPLAY_BATCH_SIZE);

        await fetchPropertyDetails(firstBatchOfNewIds, currentKey);
      }

      isFetchingRef.current = false;
    } catch (err) {
      isFetchingRef.current = false;
    }
  }, [apiHasMore, nextResultIndex, propertyIds, fetchIds, fetchPropertyDetails, currentPage, onPageChange]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Build properties array for rendering
  const properties = useMemo((): PropertyPreview[] => {
    if (status === 'filtering' || status === 'loading') {
      return [];
    }

    const visibleIds = propertyIds.slice(0, displayCount);

    return visibleIds.map((id) => ({
      id,
      isLoaded: propertyCache.has(id),
      isLoading: loadingIds.has(id),
      data: propertyCache.get(id) || null,
      error: null,
    }));
  }, [status, propertyIds, displayCount, propertyCache, loadingIds]);

  // Retry function
  const retry = useCallback(() => {
    setStatus('filtering');
  }, []);

  // Computed values
  // Show Load More button when:
  // 1. We've shown all properties from current batch (both halves of 30)
  // 2. AND API has more pages to fetch
  const shouldShowLoadMoreButton = !showingFirstHalf && apiHasMore && status === 'ready' && !isFetchingRef.current;

  // hasMore is true if either:
  // 1. We haven't shown all IDs we have (second half of 30)
  // 2. OR API has more pages
  const hasMore = displayCount < propertyIds.length || apiHasMore;

  const isInitialLoading = status === 'loading' || (status === 'filtering' && propertyIds.length === 0);
  // Only show filter loading when we already have properties displayed (not on initial load)
  const isFilterLoading = status === 'filtering' && propertyIds.length > 0;
  // Only show fetching more when we're in ready state and actively loading more details
  const isFetchingMore = status === 'ready' && (loadingIds.size > 0 || isFetchingRef.current);

  return {
    properties,
    isInitialLoading,
    isFilterLoading,
    isFetchingMore,
    hasMore,
    totalCount,
    error,
    loadMoreRef,
    retry,
    shouldShowLoadMoreButton,
    loadMore,
    currentPage,
  };
}
